import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  DollarSign,
  CreditCard,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  Plus,
  ExternalLink,
} from 'lucide-react';
import { accountingSyncService } from '@/services/financial/accountingSyncService';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface FinancialDashboardProps {
  organizationId?: string;
}

export function FinancialDashboard({ organizationId }: FinancialDashboardProps) {
  const [syncAnalytics, setSyncAnalytics] = useState<any>(null);
  const [accountingProviders, setAccountingProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadDashboardData();
  }, [organizationId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [syncAnalyticsData, accountingProvidersData] = await Promise.all([
        accountingSyncService.getSyncAnalytics(),
        accountingSyncService.getAccountingProviders(),
      ]);

      setSyncAnalytics(syncAnalyticsData);
      setAccountingProviders(accountingProvidersData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load financial dashboard data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const syncAccounting = async (providerId: string) => {
    try {
      setSyncing(providerId);
      await accountingSyncService.performFullSync(providerId);
      await loadDashboardData();

      toast({
        title: 'Success',
        description: 'Accounting sync completed successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to sync accounting data',
        variant: 'destructive',
      });
    } finally {
      setSyncing(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
      case 'succeeded':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'syncing':
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'error':
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
      case 'succeeded':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'syncing':
      case 'processing':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'error':
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading financial data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Financial Dashboard</h2>
          <p className="text-muted-foreground">Accounting integration overview</p>
        </div>
        <Button onClick={loadDashboardData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="accounting">Accounting</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Accounting Provider Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Accounting Integration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {accountingProviders.length === 0 ? (
                <div className="text-center py-6">
                  <RefreshCw className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No accounting systems connected</p>
                  <Button variant="outline" size="sm" className="mt-2">
                    <Plus className="w-4 h-4 mr-1" />
                    Connect System
                  </Button>
                </div>
              ) : (
                accountingProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <RefreshCw className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium capitalize">{provider.type}</p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={getStatusColor(provider.sync_status)}>
                            {provider.sync_status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Last sync: {formatDate(provider.last_sync)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => syncAccounting(provider.id)}
                        disabled={syncing === provider.id}
                      >
                        <RefreshCw
                          className={`w-4 h-4 ${syncing === provider.id ? 'animate-spin' : ''}`}
                        />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounting" className="space-y-4">
          {syncAnalytics && (
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Sync Success Rate</p>
                      <p className="text-2xl font-bold">{syncAnalytics.success_rate}%</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Records Synced</p>
                      <p className="text-2xl font-bold">{syncAnalytics.total_records_synced}</p>
                    </div>
                    <RefreshCw className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Sync Time</p>
                      <p className="text-2xl font-bold">{syncAnalytics.avg_sync_time}s</p>
                    </div>
                    <Clock className="w-8 h-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Last Sync</p>
                      <p className="text-sm font-bold">
                        {syncAnalytics.last_sync ? formatDate(syncAnalytics.last_sync) : 'Never'}
                      </p>
                    </div>
                    <RefreshCw className="w-8 h-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
